/**
 * "Stop Monitoring" on the pour create and edit pages: the day chips
 * (StopType '31', XHours in days). Monitoring only stops after a number of
 * days; there is no choice of how it stops.
 *
 * A saved or copied pour whose days are not one of the chips shows its days
 * as an extra, selected chip, so the value is not hidden.
 *
 * A saved pour that stops another way (after its final targets, at a set
 * time, or a legacy code) keeps it until a chip is picked, which switches it
 * to '31'.
 */
import React from 'react';
import { Alert, Box, Chip, FormHelperText, Typography } from '@mui/material';
import { QUICK_STOP_DAYS, STOP_AFTER_DAYS } from '../../utils/stopMonitoring';
import { BRAND_GREEN } from './pourWizardStyles';

export interface StopMonitoringFieldsProps {
  stopType: string;
  xHours: string;
  disabled?: boolean;
  /** Field errors keyed stopType and xHours. */
  errors?: Record<string, string | undefined>;
  /** How a saved pour that does not stop after days stops now, e.g.
   *  "At 30/10/26 09:00 AEDT" (edit page). */
  currentStopText?: string;
  onChange: (next: { stopType: string; xHours: string }) => void;
}

const StopMonitoringFields: React.FC<StopMonitoringFieldsProps> = ({
  stopType,
  xHours,
  disabled = false,
  errors = {},
  currentStopText,
  onChange,
}) => {
  const afterDays = stopType === STOP_AFTER_DAYS;
  const setDays = (days: string) => onChange({ stopType: STOP_AFTER_DAYS, xHours: days });

  const savedDays = Number(xHours);
  const chipDays =
    afterDays && Number.isInteger(savedDays) && savedDays > 0 && !QUICK_STOP_DAYS.includes(savedDays)
      ? [...QUICK_STOP_DAYS, savedDays].sort((a, b) => a - b)
      : QUICK_STOP_DAYS;

  return (
    <Box data-testid="stop-monitoring">
      <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
        Stop Monitoring
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
        {chipDays.map((days) => {
          const isActive = afterDays && xHours === String(days);
          return (
            <Chip
              key={days}
              label={`${days} Days`}
              size="small"
              aria-pressed={isActive}
              onClick={disabled ? undefined : () => setDays(String(days))}
              sx={{
                cursor: disabled ? 'default' : 'pointer',
                fontWeight: 600,
                ...(isActive
                  ? { backgroundColor: BRAND_GREEN, color: '#fff' }
                  : { backgroundColor: 'transparent', border: '1px solid', borderColor: 'grey.400' }),
              }}
            />
          );
        })}
      </Box>

      {errors.xHours && <FormHelperText error>{errors.xHours}</FormHelperText>}
      {errors.stopType && <FormHelperText error>{errors.stopType}</FormHelperText>}

      {!afterDays && currentStopText && (
        <Alert severity="info" sx={{ mt: 1.5 }}>
          This pour currently stops monitoring: {currentStopText}.
          {!disabled && ' Pick a number of days to change it.'}
        </Alert>
      )}
    </Box>
  );
};

export default StopMonitoringFields;
